import { Component, OnInit, inject, signal, computed, effect, TemplateRef, ChangeDetectionStrategy, ViewChild, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ReimbursementService } from '../../services/reimbursement.service';
import { PeriodService } from '../../../billing-management/services/period.service';
import { CatalogService } from '@services/data/catalog.service';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Global } from 'app/core/service/core/Global';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { ClaimInformePdfComponent } from 'app/feature/management-claim/components/claim-informe-pdf/claim-informe-pdf.component';
import { AdvancedFilterComponent, FilterField } from 'app/core/components/advanced-filter/advanced-filter.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { AsinetExportService, AsinetRow } from '@services/export/asinet-export.service';
import { AuthService } from '@services/core/auth.service';
import { PermissionService } from '@services/core/permission.service';

@Component({
  selector: 'app-reimbursement-management',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, ClaimInformePdfComponent, AdvancedFilterComponent, ListCardComponent, PageHeaderComponent, MobileFooterComponent],
  templateUrl: './reimbursement-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reimbursement-management.component.scss']
})
export class ReimbursementManagementComponent implements OnInit {
  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  advancedFilterFields: FilterField[] = [
    { key: 'party', label: 'Socio / Beneficiario' },
    { key: 'transactionNumber', label: 'N° Transacción' },
    { key: 'amount', label: 'Monto' },
    { key: 'entityCode', label: 'Código Entidad' }
  ];
  activeAdvancedFilters = signal<Array<{ field: string; value: string }>>([]);

  // Lista de reembolsos filtrados con filtros avanzados
  filteredReimbursements = computed(() => {
    const list = this.reimbursements() ?? [];
    const filters = this.activeAdvancedFilters() ?? [];
    if (!filters.length) return list;
    return list.filter(item => filters.every(rule => this._matchReimbursementRule(item, rule)));
  });

  // Evalúa si un reembolso cumple con una regla del filtro avanzado
  private _matchReimbursementRule(item: any, rule: { field: string; value: string }): boolean {
    const val = (rule?.value ?? '').toLowerCase();
    if (!val) return true;
    if (rule.field === 'party') return this._matchParty(item.party, val);
    if (rule.field === 'transactionNumber') return String(item.transactionNumber ?? '').toLowerCase().includes(val);
    if (rule.field === 'amount') return String(item.amount ?? '').includes(val);
    if (rule.field === 'entityCode') return String(item.entityCode ?? '').toLowerCase().includes(val);
    return true;
  }

  // Verifica coincidencia por nombre o rut del socio
  private _matchParty(party: any, val: string): boolean {
    const names = (party?.names ?? '').toLowerCase();
    const rut = (party?.rut ?? '').toLowerCase();
    return names.includes(val) || rut.includes(val);
  }

  // Signals de datos base
  periods = signal<any[]>([]);
  pdfSrc: SafeResourceUrl | null = null;
  previewType: 'pdf' | 'image' = 'pdf';
  imgSrc: string | null = null;

  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);
  allEntities = signal<any[]>([]);
  reimbursements = signal<any[]>([]);
  isLoading = signal<boolean>(true);
  hasLoadedPeriod = signal<boolean>(false);
  isGeneratingPdf = signal<boolean>(false);
  @ViewChild(ClaimInformePdfComponent) claimInformePdf?: ClaimInformePdfComponent;

  // Signals de filtros
  selectedPeriodStartId = signal<number | null>(null);
  selectedPeriodEndId = signal<number | null>(null);
  selectedEntityCode = signal<number[]>([]);
  selectedStatus = signal<string>('');
  selectedAccounted = signal<string>('');

  filteredEndPeriods: any[] = [];
  private pdfBlobUrl: string | null = null;

  // Lista de estados disponibles para reembolsos
  statuses = [
    { slug: '', name: 'Todos' },
    { slug: 'PENDING_REIMBURSEMENT', name: 'Proceso de reembolso' },
    { slug: 'REIMBURSED', name: 'Reembolsado' },
    { slug: 'PENDING_PAYMENT', name: 'Pendiente de pago' },
    { slug: 'PAID', name: 'Pagado' },
    { slug: 'REVIEWING', name: 'En revisión' },
    { slug: 'PENDING', name: 'Pendiente' },
    { slug: 'FAILED', name: 'Fallido' }
  ];

  accountedFilters = [
    { slug: '', name: 'Todos' },
    { slug: 'false', name: 'Pendiente de contabilizar' },
    { slug: 'true', name: 'Contabilizado' }
  ];

  // Computed para calcular el monto total sumado
  totalAmount = computed(() => {
    return this.filteredReimbursements().reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  });

  // Signals para modal interactivo de contabilización
  pendingReimbursements = signal<any[]>([]);
  isPendingReimbursementsLoading = signal<boolean>(false);
  /** Periodo cerrado del modal ASINET (independiente del rango de la página). */
  asinetPeriodId = signal<number | null>(null);
  closedPeriods = computed(() => this.periodService.closedBillingPeriods());
  pendingReimbursementsTotal = computed(() => {
    return this.pendingReimbursements().reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  });

  private reimbursementService = inject(ReimbursementService);
  private periodService = inject(PeriodService);
  private catalogService = inject(CatalogService);
  readonly authService = inject(AuthService);
  private asinetExport = inject(AsinetExportService);

  constructor() {
    effect(() => {
      const assigned = (this.authService.sharingEntities() as any[]) || [];
      const catalog = this.catalogService.entities() || [];
      const byCode = new Map(catalog.map((e: any) => [Number(e.code), e]));
      this.allEntities.set(
        assigned
          .map((e: any) => {
            const code = Number(e.code ?? e.entityCode);
            if (!Number.isFinite(code)) return null;
            const cat = byCode.get(code);
            const name = cat?.name ?? e.name ?? '';
            return { ...(cat || {}), ...e, code, name: `${code} - ${name}` };
          })
          .filter(Boolean) as any[]
      );
    });
    effect(() => {
      const list = this.periodService.billingPeriods();
      const rev = this.periodService.revision();
      if (!rev && !list.length) return;
      untracked(() => this.applyPeriods(list));
    });
  }

  ngOnInit(): void {
    if (!this.periodService.periods().length) this.periodService.reload();
  }

  applyPeriods(processed: any[]): void {
    this.periods.set(processed);
    if (processed.length > 0) {
      this.selectedPeriodStartId.set(processed[processed.length - 1].id);
      this.selectedPeriodEndId.set(processed[0].id);
      this.updateFilteredEndPeriods();
      this.loadReimbursements();
    }
  }

  // Actualiza los periodos finales seleccionables
  updateFilteredEndPeriods(): void {
    const startPeriod = this.periods().find(p => p.id === this.selectedPeriodStartId());
    if (!startPeriod) return;
    const startValue = startPeriod.year * 100 + startPeriod.period;
    
    this.filteredEndPeriods = this.periods().filter(p => (p.year * 100 + p.period) >= startValue);
    
    if (this.selectedPeriodEndId()) {
      const endPeriod = this.periods().find(p => p.id === this.selectedPeriodEndId());
      if (endPeriod && (endPeriod.year * 100 + endPeriod.period) < startValue) {
        this.selectedPeriodEndId.set(this.selectedPeriodStartId());
      }
    }
  }

  onPeriodStartChange(id: number): void {
    this.selectedPeriodStartId.set(id);
    this.updateFilteredEndPeriods();
    this.loadReimbursements();
  }

  // Obtiene los reembolsos aplicando los filtros activos
  loadReimbursements(): void {
    if (!this.selectedPeriodStartId() || !this.selectedPeriodEndId()) return;
    this.isLoading.set(true);
    const filters = {
      periodStartId: this.selectedPeriodStartId(),
      periodEndId: this.selectedPeriodEndId(),
      entityCodes: this.selectedEntityCode()?.length > 0 ? JSON.stringify(this.selectedEntityCode()) : undefined,
      statusSlug: this.selectedStatus() || undefined,
      accounted: this.selectedAccounted() || undefined
    };
    this.reimbursementService.list(filters).subscribe({
      next: (res) => {
        this.reimbursements.set(res);
        this.isLoading.set(false);
        this.hasLoadedPeriod.set(true);
      },
      error: () => {
        this.isLoading.set(false);
        this.hasLoadedPeriod.set(true);
      }
    });
  }

  exportExcel(): void {
    const data = this.filteredReimbursements();
    if (!data.length) {
      Swal.fire('Atención', 'No hay reembolsos para exportar con los filtros actuales', 'info');
      return;
    }
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Reembolsos');
    worksheet.columns = [
      { header: 'Entidad', key: 'entity', width: 12 },
      { header: 'Socio', key: 'party', width: 32 },
      { header: 'Tipo', key: 'type', width: 14 },
      { header: 'Periodo', key: 'period', width: 12 },
      { header: 'Monto', key: 'amount', width: 14 },
      { header: 'Estado', key: 'status', width: 14 },
      { header: 'Transacción', key: 'tx', width: 18 },
      { header: 'Fecha pago', key: 'paidAt', width: 16 },
      { header: 'Contabilizado', key: 'accounted', width: 16 }
    ];
    data.forEach((item) => {
      worksheet.addRow({
        entity: item.entityCode,
        party: item.party?.names ?? '',
        type: item.reimbursementType,
        period: `${item.year}/${String(item.period ?? '').padStart(2, '0')}`,
        amount: Number(item.amount) || 0,
        status: item.statusSlug,
        tx: item.transactionNumber ?? '',
        paidAt: item.paidAt ? String(item.paidAt).slice(0, 10) : '',
        accounted: item.accountedAt ? 'Sí' : 'No'
      });
    });
    workbook.xlsx.writeBuffer().then((buffer) => {
      this.asinetExport.saveExcelFile(buffer, `Reembolsos_${this.asinetExport.dateStamp()}`);
    });
  }

  // Abre el modal interactivo de exportación de reembolsos
  openReimbursementExportModal(content: TemplateRef<any>): void {
    const closedId = this.periodService.latestClosedPeriodId();
    if (!closedId) {
      Swal.fire('Atención', 'No hay periodos cerrados para contabilizar en ASINET.', 'info');
      return;
    }
    this.asinetPeriodId.set(closedId);
    this.modalService.open(content, { size: 'lg', backdrop: 'static' });
    this._fetchPendingReimbursements();
  }

  // Cambia el periodo cerrado del modal y recarga pendientes
  onAsinetPeriodChange(periodId: number | null): void {
    this.asinetPeriodId.set(periodId);
    this._fetchPendingReimbursements();
  }

  // Carga reembolsos pagados pendientes de contabilizar
  private _fetchPendingReimbursements(): void {
    const periodId = this.asinetPeriodId();
    if (!periodId) {
      this.pendingReimbursements.set([]);
      return;
    }
    this.isPendingReimbursementsLoading.set(true);
    const filters = this._buildPendingFilters(periodId);
    this.reimbursementService.list(filters).subscribe({
      next: (rows) => this._onPendingLoaded(rows),
      error: (err) => this._onPendingError(err)
    });
  }

  // Construye filtros para consultar reembolsos pendientes del periodo cerrado
  private _buildPendingFilters(periodId: number): any {
    const codes = this.selectedEntityCode();
    return {
      periodStartId: periodId,
      periodEndId: periodId,
      entityCodes: (codes?.length ?? 0) > 0 ? JSON.stringify(codes) : undefined,
      statusSlug: 'PAID',
      accounted: 'false'
    };
  }

  // Asigna los reembolsos pendientes obtenidos
  private _onPendingLoaded(rows: any[]): void {
    this.pendingReimbursements.set(rows ?? []);
    this.isPendingReimbursementsLoading.set(false);
  }

  // Maneja error de carga de reembolsos pendientes
  private _onPendingError(err: any): void {
    this.isPendingReimbursementsLoading.set(false);
    Swal.fire('Error', err.error?.error ?? 'Error al cargar pagos pendientes', 'error');
  }

  // Exporta copia de planilla ASINET sin marcar como contabilizado
  exportReimbursementsOnly(modalRef?: any): void {
    if (modalRef) modalRef.close();
    this._downloadAsinetWorkbook(this.pendingReimbursements());
  }

  // Confirma y ejecuta exportación y contabilización
  exportAndAccountReimbursements(modalRef?: any): void {
    const rows = this.pendingReimbursements();
    if (!rows.length) return;
    Swal.fire(this._reimbursementConfirmOptions(rows.length)).then((r) => {
      if (r.isConfirmed) this._executeAccountReimbursements(rows, modalRef);
    });
  }

  // Opciones de confirmación SweetAlert2
  private _reimbursementConfirmOptions(count: number): any {
    return {
      title: '¿Exportar y Contabilizar?',
      text: `Se exportarán ${count} pago(s) ASINET y se marcarán como contabilizados.`,
      icon: 'question' as const,
      showCancelButton: true,
      confirmButtonText: 'Sí, Contabilizar',
      cancelButtonText: 'Cancelar'
    };
  }

  // Ejecuta la descarga y llamada a API de contabilización
  private _executeAccountReimbursements(rows: any[], modalRef?: any): void {
    if (modalRef) modalRef.close();
    this._downloadAsinetWorkbook(rows);
    const uuids = rows.map((r) => r.uuid);
    this.reimbursementService.markAccounted(uuids).subscribe({
      next: () => this._onReimbursementAccountSuccess(),
      error: (err) => Swal.fire('Error', err.error?.error ?? 'Error al contabilizar', 'error')
    });
  }

  // Notifica éxito y recarga la grilla
  private _onReimbursementAccountSuccess(): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Los reembolsos quedaron contabilizados en ASINET',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true
    });
    this.loadReimbursements();
    this._fetchPendingReimbursements();
  }

  // Genera y descarga el archivo Excel ASINET
  private _downloadAsinetWorkbook(rows: any[]): void {
    const asinetRows = rows.flatMap((item) => this._buildAsinetReimbursementRows(item));
    this.asinetExport.download({
      sheetName: 'Transaction_Entry',
      rows: asinetRows,
      fileName: `Pagos_reembolsos_ASINET_${this.asinetExport.dateStamp()}`
    });
  }

  /**
   * Filas ASINET según planilla real:
   * 1) Débito gasto 4128001
   * 2) Crédito deducible 3188001 (si hay)
   * 3) Crédito transferencia 1112001
   */
  private _buildAsinetReimbursementRows(item: any): AsinetRow[] {
    const claim = item.request?.claim;
    const partyName = item.party?.names ?? 'N/A';
    const entityCode = String(item.entityCode ?? '');

    const vehicleVal = Number(claim?.vehicle?.commercialValue ?? 0);
    const isTotalLoss = claim?.claimType === 'TOTAL_LOSS';
    const grossAmount = isTotalLoss && vehicleVal > 0
      ? vehicleVal
      : (Number(claim?.totalCost ?? 0) || Number(item.amount ?? 0));
    const detailDeductible = (claim?.details ?? []).reduce((s: number, d: any) => s + Number(d.deductible ?? 0), 0);
    let deductibleAmount = Number(claim?.deductible ?? 0) || detailDeductible;
    if (isTotalLoss && grossAmount > 0 && (!(deductibleAmount > 0) || deductibleAmount < grossAmount * 0.075)) {
      deductibleAmount = Math.round(grossAmount * 0.15);
    }
    const netAmount = Math.max(0, grossAmount - deductibleAmount);
    const deductiblePct = grossAmount > 0 ? Math.round((deductibleAmount / grossAmount) * 100) : 0;
    const dateOfLoss = this.asinetExport.formatDateDot(claim?.dateOfLoss ?? claim?.details?.[0]?.dateOfLoss);
    const paidAtFormatted = this.asinetExport.formatDateDot(item.paidAt);
    const payeeName = this._transferPayeeName(item);
    const isClaim = item.reimbursementType === 'CLAIM';
    const claimDesc = isClaim
      ? `${partyName}, siniestro FPV - ${dateOfLoss}`
      : `${partyName}, Reembolso beneficio`;

    const rows: AsinetRow[] = [
      {
        AccountCode: '4128001',
        SubAccountCode: entityCode,
        FundCode: 51,
        FunctionCode: '980312',
        RestrictionCode: '2',
        EntityValue: grossAmount,
        SendMemo: 'False',
        Description: claimDesc
      }
    ];

    if (deductibleAmount > 0) {
      rows.push({
        AccountCode: '3188001',
        SubAccountCode: entityCode,
        FundCode: 51,
        FunctionCode: '980312',
        RestrictionCode: '2',
        EntityValue: deductibleAmount * -1,
        SendMemo: 'False',
        Description: `Deducible ${deductiblePct}% - ${claimDesc}`
      });
    }

    rows.push({
      AccountCode: '1112001',
      SubAccountCode: '221712270',
      FundCode: 51,
      FunctionCode: '0000',
      RestrictionCode: '0A',
      EntityValue: netAmount * -1,
      SendMemo: 'False',
      Description: `Tr. ${paidAtFormatted} ${payeeName}`
    });

    return rows;
  }

  private _campusNameForPayee(item: any): string {
    if (!item) return 'campus';
    const fromRel = item.entity?.campus || item.entity?.name;
    if (fromRel) return String(fromRel).trim();
    const entity = this.catalogService.entities().find(e => Number(e.code) === Number(item.entityCode));
    return String(entity?.campus || entity?.name || `Entidad ${item.entityCode ?? ''}`).trim();
  }

  private _transferPayeeName(item: any): string {
    if (String(item?.transferTarget || '').toUpperCase() === 'SOCIO') {
      return item?.party?.names ?? 'N/A';
    }
    return this._campusNameForPayee(item);
  }

  private _transferDestinationLabel(item: any): string {
    if (String(item?.transferTarget || '').toUpperCase() === 'SOCIO') {
      return `Al socio (${item?.party?.names || 'socio'})`;
    }
    return `Al campus (${this._campusNameForPayee(item)})`;
  }

  downloadPreviewPdf(): void {
    if (!this.pdfBlobUrl) return;
    saveAs(this.pdfBlobUrl, 'Informe_reembolso.pdf');
  }

  // Abre el mismo informe de siniestro que la ficha, más el detalle de pago
  async openPreview(item: any, template: TemplateRef<any>): Promise<void> {
    const claim = item.request?.claim;
    if (item.reimbursementType === 'CLAIM' && (claim?.uuid || claim?.id)) {
      this.isGeneratingPdf.set(true);
      try {
        await this.claimInformePdf?.openPreview({
          claim,
          party: item.party,
          reimbursement: item,
          transferCover: true
        });
      } finally {
        this.isGeneratingPdf.set(false);
      }
      return;
    }
    this._openBenefitPreview(item, template);
  }

  // Vista previa simple para reembolsos de beneficio (sin siniestro)
  private _openBenefitPreview(item: any, template: TemplateRef<any>): void {
    this.previewType = 'pdf';
    this.imgSrc = null;
    const doc = new jsPDF('p', 'mm', 'letter');
    const entName = item.party ? `${item.party.entityCode} - ${this._getEntityName(item.party.entityCode)}` : '';
    let y = this._addHeader(doc, entName, 'INFORME DE REEMBOLSO');
    y = this._drawPartyTable(doc, item.party, y);
    this._drawPaymentTable(doc, item, y);
    const pdfBlob = doc.output('blob');
    if (this.pdfBlobUrl) URL.revokeObjectURL(this.pdfBlobUrl);
    this.pdfBlobUrl = URL.createObjectURL(pdfBlob);
    this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
    this.modalService.open(template, { size: 'xl', backdrop: 'static' });
  }

  // Abre la vista previa del comprobante físico de pago del reembolso
  viewReceipt(item: any, template: TemplateRef<any>): void {
    const docObj = item.documents?.[0]?.document;
    if (!docObj) return;
    const url = `${Global.url}V1/documents/view/${docObj.uuid}`;
    const isImg = docObj.mimeType?.startsWith('image/');
    this.previewType = isImg ? 'image' : 'pdf';
    this.imgSrc = isImg ? url : null;
    this.pdfSrc = isImg ? null : this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.modalService.open(template, { size: 'xl', backdrop: 'static' });
  }

  private _getEntityName(code: any): string {
    return this.allEntities().find(e => e.code === code)?.name || '';
  }

  private _addHeader(doc: jsPDF, entity: string, title: string): number {
    doc.setFontSize(14).setFont('helvetica', 'bold').text('Fondo de Protección Vehicular', 20, 20);
    doc.setFontSize(10).setFont('helvetica', 'italic').text(entity || 'N/A', 20, 26);
    doc.setFontSize(12).setFont('helvetica', 'bold').text(title, 20, 35);
    return 42;
  }

  private _drawPartyTable(doc: jsPDF, party: any, y: number): number {
    autoTable(doc, {
      startY: y, head: [['DATOS DEL SOCIO', '']],
      body: [['Nombre', party?.names ?? 'N/A'], ['RUT', party?.rut ?? 'N/A'], ['Correo', party?.mail ?? 'N/A'], ['Teléfono', party?.mobileNumber ?? 'N/A']],
      theme: 'grid', headStyles: { fillColor: [52, 152, 219] }
    });
    return (doc as any).lastAutoTable.finalY + 10;
  }

  private _drawPaymentTable(doc: jsPDF, item: any, y: number): number {
    const fmt = (val?: number) => `$${new Intl.NumberFormat('es-CL').format(val ?? 0)}`;
    autoTable(doc, {
      startY: y, head: [['DETALLE DE TRANSACCIÓN DE PAGO', '']],
      body: [
        ['ID Reembolso', item.uuid],
        ['Nro Transacción', item.transactionNumber ?? 'N/A'],
        ['Fecha de Pago', item.paidAt ?? 'N/A'],
        ['Transferencia', this._transferDestinationLabel(item)],
        ['Monto Pagado (neto)', fmt(item.amount)],
        ['Contabilizado', item.accountedAt ? 'Sí' : 'No']
      ],
      theme: 'grid', headStyles: { fillColor: [44, 62, 80] }
    });
    return (doc as any).lastAutoTable.finalY + 10;
  }
}
